class AATemplateBuilder {
  constructor(root) {
    this.root = root;
    this.clientStorageKey = 'aa-headquarters-clients';
    this.templateStorageKey = 'aa-template-builder-drafts';
    this.clients = [];
    this.templates = [];
    this.builderMessage = '';
    this.expandedFieldId = '';
    this.isAddingCustomSection = false;

    this.draftStatus = 'saved';
    this.lastDraftSavedAt = null;
    this.draftSaveTimer = null;
    this.baseRevision = 0;
    this.publishedAt = null;
    this.publishMonth =
      this.getDefaultPublishMonth();
    this.isPublishing = false;

    this.previewDevice = 'desktop';
    this.draggedLayoutField = null;
    this.resizingLayoutField = null;

    this.template = this.getDefaultTemplate();

    this.init();
  }

  async init() {
    await Promise.all([
      this.loadClients(),
      this.loadTemplates()
    ]);

    this.applyUrlClient();

    if (this.template.clientId) {
      await this.loadClientTemplate(
        this.template.clientId
      );
    }

    this.bindActions();
    this.bindLayoutInteractions();
    this.render();
  }

  escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  getDefaultPublishMonth() {
    const now = new Date();

    const monthsAhead =
      now.getDate() <= 15
        ? 1
        : 2;

    const date = new Date(
      now.getFullYear(),
      now.getMonth() + monthsAhead,
      1
    );

    return (
      `${date.getFullYear()}-` +
      `${String(
        date.getMonth() + 1
      ).padStart(2, '0')}`
    );
  }

  async loadClients() {
    const db = window.AASupabaseClient.getClient();

    if (!db) {
      this.clients = [];
      return;
    }

    const { data, error } = await db
      .from('aa_clients')
      .select(`
        id,
        business_name,
        client_code,
        active_status,
        account_status,
        invoice_status,
        client_configuration
      `)
      .or('active_status.is.null,active_status.neq.archived')
      .order('business_name', { ascending: true });

    if (error) {
      console.error('Unable to load clients.', error);
      this.clients = [];
      return;
    }

    this.clients = (data || []).map((client) => ({
      ...client,
      client_code:
        client.client_code || ''
    }));
  }

  async loadClientTemplate(clientId) {
    window.clearTimeout(
      this.draftSaveTimer
    );

    this.draftSaveTimer = null;
    this.draftStatus = 'saved';
    this.lastDraftSavedAt = null;
    this.baseRevision = 0;
    this.publishedAt = null;
    this.publishMonth =
      this.getDefaultPublishMonth();

    if (!clientId) {
      this.template =
        this.getDefaultTemplate();

      this.builderMessage = '';
      this.render();
      return;
    }

    if (
      !window.AAPlaybookService
        ?.getClientTemplateConfiguration
    ) {
      this.builderMessage =
        'Unable to load the client template.';

      this.render();
      return;
    }

    const db =
      window.AASupabaseClient
        .getClient();

    const [configuration, draftResult] =
      await Promise.all([
        window.AAPlaybookService
          .getClientTemplateConfiguration({
            clientId
          }),
        db
          ? db
              .from('aa_template_drafts')
              .select('*')
              .eq('client_id', clientId)
              .maybeSingle()
          : Promise.resolve({
              data: null,
              error: null
            })
      ]);

    if (draftResult?.error) {
      console.error(
        'Unable to load template draft.',
        draftResult.error
      );
    }

    const publishedSections =
      Array.isArray(
        configuration?.sections
      ) &&
      configuration.sections.length
        ? this.clone(
            configuration.sections
          )
        : this.getSectionLibrary();

    const publishedRevision =
      Number(
        configuration?.template
          ?.template_revision || 0
      );

    const draft =
      draftResult?.data || null;

    const draftSections =
      Array.isArray(
        draft?.draft_config
          ?.sections
      )
        ? draft.draft_config.sections
        : [];

    const sections =
      draftSections.length
        ? this.clone(draftSections)
        : publishedSections;

    const draftSelectedSectionId =
      draft?.draft_config
        ?.selectedSectionId;

    this.template = {
      ...this.getDefaultTemplate(),

      templateId:
        configuration?.template?.id ||
        draft?.base_template_id ||
        '',

      templateName:
        draft?.draft_name ||
        configuration?.template
          ?.template_name ||
        '',

      clientId,

      sections,

      selectedSectionId:
        (
          draftSelectedSectionId &&
          sections.some(
            (section) =>
              section.id ===
              draftSelectedSectionId
          )
        )
          ? draftSelectedSectionId
          : (
              sections.find(
                (section) =>
                  section.enabled
              )?.id ||
              sections[0]?.id ||
              ''
            )
    };

    this.baseRevision =
      Number(
        draft?.base_revision ??
        publishedRevision ??
        0
      );

    this.publishedAt =
      configuration?.template
        ?.published_at ||
      null;

    this.lastDraftSavedAt =
      draft?.updated_at ||
      null;

    this.draftStatus =
      draft
        ? 'draft'
        : 'saved';

    this.builderMessage =
      draft
        ? 'Draft restored.'
        : '';

    this.render();
  }

  async loadTemplates() {
    const db = window.AASupabaseClient.getClient();

    if (!db) {
      this.templates = [];
      return;
    }

    const { data, error } = await db
      .from('aa_workspace_templates')
      .select('*')
      .eq('is_active', true)
      .order('template_name');

    if (error) {
      console.error('Unable to load templates.', error);
      this.templates = [];
      return;
    }

    this.templates = (data || []).map((template) => ({
      ...template.template_config,
      templateId: template.id,
      templateName: template.template_name,
      templateType: template.template_type
    }));
  }

  getDraftPayload() {
    this.prepareAllLayouts();

    return {
      ...this.clone(this.template),
      templateId: undefined
    };
  }

  getDraftStatusLabel() {
    if (this.draftStatus === 'saving') {
      return 'Saving draft...';
    }

    if (this.draftStatus === 'error') {
      return 'Draft not saved';
    }

    if (this.draftStatus === 'draft') {
      return this.lastDraftSavedAt
        ? 'Draft saved'
        : 'Draft';
    }

    return this.baseRevision
      ? `Published v${this.baseRevision}`
      : 'Not published';
  }

  scheduleDraftSave() {
    if (!this.template.clientId) {
      return;
    }

    this.draftStatus = 'saving';

    window.clearTimeout(
      this.draftSaveTimer
    );

    this.draftSaveTimer =
      window.setTimeout(
        () => {
          this.saveDraft();
        },
        650
      );
  }

  async saveDraft() {
    if (!this.template.clientId) {
      return false;
    }

    const db =
      window.AASupabaseClient
        .getClient();

    if (!db) {
      this.draftStatus = 'error';
      this.render();
      return false;
    }

    this.draftStatus = 'saving';

    const auth =
      await window.AAWorkspaceContext
        ?.getWorkspaceAuthContext?.();

    const now =
      new Date().toISOString();

    const { data, error } =
      await db
        .from('aa_template_drafts')
        .upsert(
          {
            client_id:
              this.template.clientId,

            base_template_id:
              this.template.templateId ||
              null,

            base_revision:
              this.baseRevision || null,

            draft_name:
              this.getTemplateName(),

            draft_config:
              this.getDraftPayload(),

            updated_by:
              auth?.user?.id || null,

            updated_at: now
          },
          {
            onConflict: 'client_id'
          }
        )
        .select()
        .single();

    if (error) {
      console.error(
        'Unable to save template draft.',
        error
      );

      this.draftStatus = 'error';
      this.render();
      return false;
    }

    this.lastDraftSavedAt =
      data?.updated_at || now;

    this.draftStatus = 'draft';
    this.render();

    return true;
  }

  getPublishEffectiveDate() {
    const month =
      this.publishMonth ||
      this.getDefaultPublishMonth();

    return `${month}-01`;
  }

  async publishTemplateChanges() {
    if (
      !this.template.clientId ||
      this.isPublishing
    ) {
      return;
    }

    this.isPublishing = true;
    this.builderMessage =
      'Publishing changes...';
    this.render();

    window.clearTimeout(
      this.draftSaveTimer
    );

    const draftSaved =
      await this.saveDraft();

    if (!draftSaved) {
      this.isPublishing = false;
      this.builderMessage =
        'Draft could not be saved. Publishing stopped.';
      this.render();
      return;
    }

    const db =
      window.AASupabaseClient
        .getClient();

    if (!db) {
      this.isPublishing = false;
      this.builderMessage =
        'Supabase is unavailable.';
      this.render();
      return;
    }

    const {
      data,
      error
    } =
      await db.rpc(
        'aa_publish_client_template',
        {
          p_client_id:
            this.template.clientId,

          p_template_id:
            this.template.templateId ||
            null,

          p_template_name:
            this.getTemplateName(),

          p_template_config:
            this.getDraftPayload(),

          p_effective_month:
            this.getPublishEffectiveDate()
        }
      );

    if (error || !data) {
      console.error(
        'Unable to publish template.',
        error
      );

      this.isPublishing = false;
      this.builderMessage =
        error?.message ||
        'Template could not be published.';
      this.render();
      return;
    }

    this.template = {
      ...this.template,
      templateId:
        data.templateId ||
        this.template.templateId,

      templateName:
        data.templateName ||
        this.getTemplateName(),

      templateVersion:
        `v${data.templateRevision || 1}`
    };

    this.baseRevision =
      Number(
        data.templateRevision || 1
      );

    this.publishedAt =
      data.publishedAt ||
      new Date().toISOString();

    this.lastDraftSavedAt =
      null;

    this.draftStatus =
      'saved';

    this.isPublishing =
      false;

    this.builderMessage =
      `Published v${this.baseRevision} starting ${data.effectiveMonth || this.publishMonth}. ${Number(data.affectedPlaybooks || 0)} future Playbook${Number(data.affectedPlaybooks || 0) === 1 ? '' : 's'} updated.`;

    const selectedClient =
      this.clients.find(
        (client) =>
          String(client.id) ===
          String(this.template.clientId)
      );

    if (
      String(
        selectedClient?.active_status || ''
      )
        .trim()
        .toLowerCase() ===
        'active'
    ) {
      await this.ensurePlaybookWindow();
    }

    this.render();
  }

  async saveTemplate(template) {
    const db = window.AASupabaseClient.getClient();

    if (!db) {
      return {
        success: false,
        error: 'Supabase client is unavailable.'
      };
    }

    const payload = {
      template_name: this.getTemplateName(),
      template_type: 'monthly_marketing',
      template_config: {
        ...this.clone(template),
        templateId: undefined
      },
      is_active: true,
      updated_at: new Date().toISOString()
    };

    let result;

    if (template.templateId) {
      result = await db
        .from('aa_workspace_templates')
        .update(payload)
        .eq('id', template.templateId)
        .select()
        .single();
    } else {
      result = await db
        .from('aa_workspace_templates')
        .insert(payload)
        .select()
        .single();
    }

    if (result.error) {
      console.error('Unable to save template.', result.error);

      return {
        success: false,
        error: result.error
      };
    }

    return {
      success: true,
      template: result.data
    };
  }

  async saveTemplateAssignment({ templateId }) {
  const db = window.AASupabaseClient.getClient();

  if (!db || !this.template.clientId || !templateId) {
    return {
      success: false,
      error: 'Client and template are required.'
    };
  }

  const { data: existingAssignments, error: lookupError } = await db
    .from('aa_client_template_assignments')
    .select('*')
    .eq('client_id', this.template.clientId)
    .order('updated_at', { ascending: false })
    .limit(1);

  if (lookupError) {
    console.error(
      'Unable to check client template assignment.',
      lookupError
    );

    return {
      success: false,
      error: lookupError
    };
  }

  const existingAssignment =
    existingAssignments?.[0] || null;

  const payload = {
    client_id: this.template.clientId,
    template_id: templateId,

    assigned_month:
      existingAssignment?.assigned_month ||
      `${new Date().toISOString().slice(0, 7)}-01`,

    assignment_status: 'assigned',

    assignment_config: {
      templateVersion:
        this.template.templateVersion || 'v1',

      sections:
        this.clone(this.template.sections)
    },

    updated_at:
      new Date().toISOString()
  };

  let result;

  if (existingAssignment?.id) {
    result = await db
      .from('aa_client_template_assignments')
      .update(payload)
      .eq('id', existingAssignment.id)
      .select()
      .single();
  } else {
    result = await db
      .from('aa_client_template_assignments')
      .insert(payload)
      .select()
      .single();
  }

  if (result.error) {
    console.error(
      'Unable to save client template assignment.',
      result.error
    );

    return {
      success: false,
      error: result.error
    };
  }

  return {
    success: true,
    assignment: result.data
  };
}

  async ensurePlaybookWindow() {
  const db = window.AASupabaseClient.getClient();

  if (!db || !this.template.clientId) {
    return {
      success: false,
      error: 'Client is required to create Playbooks.'
    };
  }

  const { data, error } = await db.functions.invoke(
    'aa-playbook-service',
    {
      body: {
        action: 'ensure-playbook-window',
        clientId: this.template.clientId
      }
    }
  );

  if (error) {
    console.error(
      'Unable to create Playbook window.',
      error
    );

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    data
  };
}

  applyUrlClient() {
    const params = new URLSearchParams(window.location.search);
    const clientId = params.get('client');

    if (clientId) {
      this.template.clientId = clientId;
    }
  }

  loadSavedTemplate(templateId) {
  const currentClientId = this.template.clientId;

  if (!templateId) {
    this.template = {
      ...this.getDefaultTemplate(),
      clientId: currentClientId
    };

    this.builderMessage = 'New template started.';
    this.render();
    return;
  }

    const savedTemplate = this.templates.find(
      (template) => String(template.templateId) === String(templateId)
    );

    if (!savedTemplate) {
      this.builderMessage = 'Saved template could not be found.';
      this.render();
      return;
    }

    this.template = {
      ...this.getDefaultTemplate(),
      ...this.clone(savedTemplate),
      templateId: savedTemplate.templateId,
      templateName: savedTemplate.templateName,
      clientId: currentClientId || savedTemplate.clientId || '',
sections: Array.isArray(savedTemplate.sections)
        ? this.clone(savedTemplate.sections)
        : this.getSectionLibrary()
    };

    if (
      !this.template.selectedSectionId ||
      !this.template.sections.some(
        (section) => section.id === this.template.selectedSectionId
      )
    ) {
      this.template.selectedSectionId =
        this.template.sections[0]?.id || '';
    }

    this.builderMessage = `Editing ${this.template.templateName}.`;
    this.render();
  }

  getFieldTypes() {
    return [
      'Single Line Text',
      'Paragraph',
      'Number',
      'Currency',
      'Date',
      'Time',
      'URL',
      'Email',
      'Phone',
      'Dropdown',
      'Multi Select',
      'Checkbox',
      'Toggle',
      'Image Upload',
      'File Upload',
      'Rich Text',
      'Heading',
      'Divider'
    ];
  }

  getLayoutOptions() {
    return ['Creative Brief', 'Marketing Cards', 'Table/Grid', 'Narrative'];
  }

  createField(label, type = 'Single Line Text', width = '50%') {
    return {
      id: `field-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      label,
      type,
      required: false,
      placeholder: '',
      helpText: '',
      width,
      startNewRow: false,
      options: ''
    };
  }

  getDefaultFieldsForSection(title) {
    const fields = {
      Coupons: [
        ['Coupon Name', 'Single Line Text', '50%'],
        ['Start Date', 'Date', '25%'],
        ['End Date', 'Date', '25%'],
        ['Discount', 'Single Line Text', '50%'],
        ['Coupon Image', 'Image Upload', '50%'],
        ['Description', 'Paragraph', '100%']
      ],
      Classes: [
        ['Class Name', 'Single Line Text', '50%'],
        ['Date', 'Date', '25%'],
        ['Start Time', 'Time', '25%'],
        ['End Time', 'Time', '25%'],
        ['Registration Link', 'URL', '50%'],
        ['Price', 'Currency', '25%'],
        ['Class Image', 'Image Upload', '50%'],
        ['Description', 'Paragraph', '100%']
      ],
      Events: [
        ['Event Name', 'Single Line Text', '50%'],
        ['Date', 'Date', '25%'],
        ['Start Time', 'Time', '25%'],
        ['End Time', 'Time', '25%'],
        ['Event Link', 'URL', '50%'],
        ['Event Image', 'Image Upload', '50%'],
        ['Description', 'Paragraph', '100%']
      ],
      'Machine Spotlight': [
        ['Machine Name', 'Single Line Text', '50%'],
        ['Brand', 'Single Line Text', '25%'],
        ['Price', 'Currency', '25%'],
        ['Image', 'Image Upload', '50%'],
        ['Details', 'Paragraph', '100%']
      ],
      'Previously Loved': [
        ['Machine Name', 'Single Line Text', '50%'],
        ['Brand', 'Single Line Text', '25%'],
        ['Price', 'Currency', '25%'],
        ['Image', 'Image Upload', '50%'],
        ['Description', 'Paragraph', '100%']
      ]
    };

    return (fields[title] || [
      ['Title', 'Single Line Text', '50%'],
      ['Date', 'Date', '25%'],
      ['Image', 'Image Upload', '50%'],
      ['Description', 'Paragraph', '100%']
    ]).map(([label, type, width]) => this.createField(label, type, width));
  }

  getSectionLibrary() {
    return [
      'Cover Theme',
      'Coupons',
      'Classes',
      'Events',
      'Machine Spotlight',
      'Previously Loved',
      'Email Requests',
      'New Fabric Collections',
      'Notions & Accessories',
      'Overstock',
      'Special Events',
      'Special Sales',
      'Foot of the Month',
      'Customer & Team Spotlights',
      'Weekly Web Banner',
      'Looking Forward',
      'Additional Notes'
    ].map((title, index) => ({
      id: title.toLowerCase().replaceAll('&', 'and').replaceAll(' ', '-').replaceAll('’', ''),
      title,
      displayOrder: index + 1,
      enabled: index < 4,
      activeTab: 'fields',
      layoutType: index === 0 ? 'Creative Brief' : 'Marketing Cards',
      required: false,
      helpText: '',
      rowRules: {
        repeatable: title !== 'Cover Theme',
        minimumRows: 1,
        maximumRows: title === 'Coupons' || title === 'Classes' || title === 'Events' ? 8 : 3,
        defaultRows: 1,
        allowDuplicateRows: true,
        allowDeleteRows: true,
        allowReorderRows: true,
        rowLabelText: title === 'Classes'
          ? 'Class'
          : title === 'Events'
            ? 'Event'
            : title === 'Coupons'
              ? 'Coupon'
              : title.replace(/s$/, ''),
        addButtonText: `Add ${
          title === 'Classes'
            ? 'Class'
            : title.replace(/s$/, '')
        }`
      },
      fields: this.getDefaultFieldsForSection(title)
    }));
  }

  getDefaultTemplate() {
  return {
    templateId: '',
    templateName: '',
    templateVersion: 'v1',
    clientId: '',
    selectedSectionId: 'classes',
    sections: this.getSectionLibrary()
  };
}

  getSelectedClientName() {
    const client = this.clients.find(
      (item) =>
        String(item.id) ===
        String(this.template.clientId)
    );

    if (!client) {
      return 'Select Client';
    }

    return client.client_code
      ? `${client.client_code} · ${client.business_name}`
      : client.business_name;
  }

  getTemplateName() {
    const client =
      this.clients.find(
        (item) =>
          String(item.id) ===
          String(this.template.clientId)
      );

    if (!client) {
      return 'Monthly Marketing Playbook Template';
    }

    return `${client.business_name} Monthly Marketing Playbook Template`;
  }

  getFieldWidthLabel(width) {
    const normalized =
      String(width || '50%');

    const labels = {
      '25%': 'Quarter',
      '50%': 'Half',
      '75%': 'Three-quarter',
      '100%': 'Full'
    };

    return labels[normalized] || normalized;
  }

  getFieldSpan(width) {
    switch (String(width || '50%')) {
      case '25%':
        return 1;
      case '75%':
        return 3;
      case '100%':
        return 4;
      default:
        return 2;
    }
  }

  getLayoutColumns(device = this.previewDevice) {
    return device === 'mobile' ? 2 : 4;
  }

  getLayoutSpanFromWidth(width, device = 'desktop') {
    const columns =
      this.getLayoutColumns(device);

    if (device === 'mobile') {
      return columns;
    }

    return Math.min(
      this.getFieldSpan(width),
      columns
    );
  }

  getLayoutWidthFromSpan(span) {
    const widths = {
      1: '25%',
      2: '50%',
      3: '75%',
      4: '100%'
    };

    return widths[Number(span)] || '50%';
  }

  layoutsOverlap(a, b) {
    if (!a || !b) {
      return false;
    }

    if (Number(a.row) !== Number(b.row)) {
      return false;
    }

    const aStart =
      Number(a.col);

    const aEnd =
      aStart +
      Number(a.span) -
      1;

    const bStart =
      Number(b.col);

    const bEnd =
      bStart +
      Number(b.span) -
      1;

    return !(
      aEnd < bStart ||
      bEnd < aStart
    );
  }

  findOpenLayoutPosition(
    section,
    device,
    span,
    excludeFieldId = '',
    startRow = 1
  ) {
    const columns =
      this.getLayoutColumns(device);

    const safeSpan =
      Math.max(
        1,
        Math.min(Number(span) || 1, columns)
      );

    const fields =
      Array.isArray(section?.fields)
        ? section.fields
        : [];

    for (
      let row = Math.max(1, Number(startRow) || 1);
      row < 50;
      row += 1
    ) {
      for (
        let col = 1;
        col <= columns - safeSpan + 1;
        col += 1
      ) {
        const candidate = {
          row,
          col,
          span: safeSpan
        };

        const occupied =
          fields.some((field) => {
            if (
              String(field.id) ===
              String(excludeFieldId)
            ) {
              return false;
            }

            const layout =
              field.layout?.[device];

            return (
              layout &&
              this.layoutsOverlap(
                candidate,
                layout
              )
            );
          });

        if (!occupied) {
          return candidate;
        }
      }
    }

    return {
      row: 1,
      col: 1,
      span: safeSpan
    };
  }

  ensureSectionLayouts(section) {
    if (
      !section ||
      !Array.isArray(section.fields)
    ) {
      return;
    }

    ['desktop', 'mobile'].forEach(
      (device) => {
        const columns =
          this.getLayoutColumns(device);

        section.fields.forEach(
          (field) => {
            field.layout =
              field.layout &&
              typeof field.layout === 'object'
                ? field.layout
                : {};

            const current =
              field.layout[device];

            if (
              current &&
              Number(current.row) >= 1 &&
              Number(current.col) >= 1 &&
              Number(current.span) >= 1
            ) {
              current.span =
                Math.min(
                  Number(current.span),
                  columns
                );

              current.col =
                Math.min(
                  Number(current.col),
                  columns -
                    current.span +
                    1
                );

              return;
            }

            const span =
              device === 'desktop'
                ? this.getLayoutSpanFromWidth(
                    field.width,
                    device
                  )
                : columns;

            const position =
              this.findOpenLayoutPosition(
                section,
                device,
                span,
                field.id,
                1
              );

            field.layout[device] =
              position;
          }
        );
      }
    );
  }

  prepareAllLayouts() {
    (this.template.sections || [])
      .forEach((section) => {
        this.ensureSectionLayouts(section);
      });
  }

  getFieldLayout(
    section,
    field,
    device = this.previewDevice
  ) {
    this.ensureSectionLayouts(section);

    return (
      field.layout?.[device] || {
        row: 1,
        col: 1,
        span:
          this.getLayoutColumns(device)
      }
    );
  }

  getLayoutMaxRow(
    section,
    device = this.previewDevice
  ) {
    this.ensureSectionLayouts(section);

    return Math.max(
      1,
      ...section.fields.map(
        (field) =>
          Number(
            field.layout?.[device]?.row || 1
          )
      )
    );
  }

  resolveLayoutCollisions(
    section,
    field,
    device
  ) {
    const fields =
      section.fields || [];

    let active =
      field.layout?.[device];

    if (!active) {
      return;
    }

    let changed = true;
    let safety = 0;

    while (changed && safety < 100) {
      changed = false;
      safety += 1;

      for (const other of fields) {
        if (
          String(other.id) ===
          String(field.id)
        ) {
          continue;
        }

        const otherLayout =
          other.layout?.[device];

        if (
          otherLayout &&
          this.layoutsOverlap(
            active,
            otherLayout
          )
        ) {
          const next =
            this.findOpenLayoutPosition(
              section,
              device,
              otherLayout.span,
              other.id,
              Number(active.row) + 1
            );

          other.layout[device] =
            next;

          changed = true;
        }
      }
    }
  }

  placeLayoutField(
    sectionId,
    fieldId,
    device,
    row,
    col
  ) {
    const section =
      this.template.sections.find(
        (item) =>
          String(item.id) ===
          String(sectionId)
      );

    const field =
      section?.fields?.find(
        (item) =>
          String(item.id) ===
          String(fieldId)
      );

    if (!section || !field) {
      return;
    }

    this.ensureSectionLayouts(section);

    const columns =
      this.getLayoutColumns(device);

    const current =
      this.getFieldLayout(
        section,
        field,
        device
      );

    const span =
      Math.min(
        Number(current.span) || 1,
        columns
      );

    field.layout[device] = {
      row:
        Math.max(
          1,
          Number(row) || 1
        ),

      col:
        Math.max(
          1,
          Math.min(
            Number(col) || 1,
            columns - span + 1
          )
        ),

      span
    };

    if (device === 'desktop') {
      field.width =
        this.getLayoutWidthFromSpan(
          span
        );

      field.startNewRow =
        field.layout.desktop.col === 1;
    }

    this.resolveLayoutCollisions(
      section,
      field,
      device
    );

    this.scheduleDraftSave();
    this.render();
  }

  resizeLayoutField(
    sectionId,
    fieldId,
    device,
    span
  ) {
    const section =
      this.template.sections.find(
        (item) =>
          String(item.id) ===
          String(sectionId)
      );

    const field =
      section?.fields?.find(
        (item) =>
          String(item.id) ===
          String(fieldId)
      );

    if (!section || !field) {
      return;
    }

    this.ensureSectionLayouts(section);

    const columns =
      this.getLayoutColumns(device);

    const layout =
      this.getFieldLayout(
        section,
        field,
        device
      );

    const safeSpan =
      Math.max(
        1,
        Math.min(
          Number(span) || 1,
          columns
        )
      );

    layout.span =
      safeSpan;

    layout.col =
      Math.min(
        layout.col,
        columns - safeSpan + 1
      );

    if (device === 'desktop') {
      field.width =
        this.getLayoutWidthFromSpan(
          safeSpan
        );

      field.startNewRow =
        layout.col === 1;
    }

    this.resolveLayoutCollisions(
      section,
      field,
      device
    );

    this.scheduleDraftSave();
    this.render();
  }

  getEnabledSections() {
    return [...this.template.sections]
      .filter((section) => section.enabled)
      .sort((a, b) => Number(a.displayOrder) - Number(b.displayOrder));
  }

  getSelectedSection() {
    return this.template.sections.find((section) =>
      section.id === this.template.selectedSectionId
    ) || this.template.sections[0];
  }

  getSummary() {
    const enabledSections = this.getEnabledSections();
    const fields = enabledSections.flatMap((section) => section.fields || []);

    return {
      sections: enabledSections.length,
      fields: fields.length,
      rows: enabledSections.reduce((total, section) => total + Number(section.rowRules?.maximumRows || 1), 0),
      required: fields.filter((field) => field.required).length
    };
  }

    async assignTemplateToClient() {
    if (!this.template.clientId) {
      this.builderMessage = 'Select a client before saving.';
      this.render();
      return;
    }


    this.builderMessage =
  'Saving client template...';

    this.render();

    const templateName = this.getTemplateName();

    const templateResult = await this.saveTemplate({
      ...this.template,
      templateName
    });

    if (!templateResult.success) {
      this.builderMessage = 'Template could not be saved. Check the console for details.';
      this.render();
      return;
    }

    const savedTemplate = templateResult.template;

    this.template = {
  ...this.template,
  templateId: savedTemplate.id,
  templateName: savedTemplate.template_name
};

    this.templates = [
      ...this.templates.filter((template) => template.templateId !== savedTemplate.id),
      this.clone(this.template)
    ];

    const assignmentResult = await this.saveTemplateAssignment({
  templateId: savedTemplate.id
});

    if (!assignmentResult.success) {
      this.builderMessage = 'Template saved, but the client assignment failed. Check the console.';
      this.render();
      return;
    }

    const playbookResult =
  await this.ensurePlaybookWindow();

if (!playbookResult.success) {
  this.builderMessage =
    'Client template saved, but the Playbook window could not be prepared. Check the console.';

  this.render();
  return;
}

this.builderMessage =
  `Client template saved for ${this.getSelectedClientName()}.`;

this.render();
  }

  bindActions() {
    this.root.addEventListener('submit', (event) => {
      const customSectionForm = event.target.closest(
        '[data-aa-template-custom-section-form]'
      );

      if (customSectionForm) {
        event.preventDefault();

        const title = customSectionForm
          .querySelector('[data-aa-template-custom-section-name]')
          ?.value.trim();

        if (!title) return;

        const id = `custom-${Date.now()}`;

        this.template.sections.push({
          id,
          title,
          displayOrder: this.template.sections.length + 1,
          enabled: true,
          activeTab: 'fields',
          layoutType: 'Marketing Cards',
          required: false,
          helpText: '',
          rowRules: {
            repeatable: true,
            minimumRows: 1,
            maximumRows: 3,
            defaultRows: 1,
            allowDuplicateRows: true,
            allowDeleteRows: true,
            allowReorderRows: true,
            rowLabelText: title,
            addButtonText: `Add ${title}`
          },
          fields: []
        });

        this.template.selectedSectionId = id;
        this.isAddingCustomSection = false;
        this.scheduleDraftSave();
        this.render();
        return;
      }

      const addFieldForm = event.target.closest('[data-aa-template-add-field-form]');
      if (!addFieldForm) return;

      event.preventDefault();

      const section = this.template.sections.find((item) => item.id === addFieldForm.dataset.aaTemplateAddFieldForm);
      const label = addFieldForm.querySelector('[data-aa-template-new-field-label]')?.value.trim();
      const type = addFieldForm.querySelector('[data-aa-template-new-field-type]')?.value || 'Single Line Text';

      if (!section || !label) return;

      section.fields.push(this.createField(label, type, type === 'Paragraph' || type === 'Rich Text' ? '100%' : '50%'));
      this.scheduleDraftSave();
      this.render();
    });

    this.root.addEventListener('change', async (event) => {
            const savedTemplateSelect = event.target.closest('[data-aa-template-select-saved]');
      const sectionPicker = event.target.closest('[data-aa-template-section-picker]');
      const templateField = event.target.closest('[data-aa-template-field]');
      const sectionField = event.target.closest('[data-aa-template-section-field]');
      const fieldInput = event.target.closest('[data-aa-template-field-property]');
      const publishMonthField =
        event.target.closest(
          '[data-aa-template-publish-month]'
        );

      if (publishMonthField) {
        this.publishMonth =
          publishMonthField.value ||
          this.getDefaultPublishMonth();

        this.render();
        return;
      }
            if (savedTemplateSelect) {
        this.loadSavedTemplate(savedTemplateSelect.value);
        return;
      }

            if (sectionPicker) {
        this.template.selectedSectionId = sectionPicker.value;
        this.render();
        return;
      }

      const newRowField =
        event.target.closest(
          '[data-aa-template-field-new-row]'
        );

      if (newRowField) {
        const section =
          this.template.sections.find(
            (item) =>
              item.id ===
              newRowField.dataset
                .aaTemplateFieldSection
          );

        const field =
          section?.fields.find(
            (item) =>
              item.id ===
              newRowField.dataset
                .aaTemplateFieldNewRow
          );

        if (field) {
          field.startNewRow =
            newRowField.checked;

          this.scheduleDraftSave();
          this.render();
        }

        return;
      }

      if (fieldInput) {
        const section = this.template.sections.find(
          (item) => item.id === fieldInput.dataset.aaTemplateFieldSection
        );

        const field = section?.fields.find(
          (item) => item.id === fieldInput.dataset.aaTemplateField
        );

        if (!field) return;

        const property = fieldInput.dataset.aaTemplateFieldProperty;

        field[property] = fieldInput.type === 'checkbox'
          ? fieldInput.checked
          : fieldInput.value;

        this.scheduleDraftSave();
        this.render();
        return;
      }

      if (sectionField) {
        const section = this.template.sections.find(
          (item) => item.id === sectionField.dataset.aaTemplateSection
        );

        if (!section) return;

        const field = sectionField.dataset.aaTemplateSectionField;
        const value = sectionField.type === 'checkbox'
          ? sectionField.checked
          : sectionField.value;

        if (field.startsWith('rowRules.')) {
          section.rowRules[field.replace('rowRules.', '')] = value;
        } else {
          section[field] = value;
        }

        if (
          field === 'enabled' &&
          value === false &&
          this.template.selectedSectionId ===
            section.id
        ) {
          this.template.selectedSectionId =
            this.template.sections.find(
              (item) =>
                item.enabled &&
                item.id !== section.id
            )?.id || '';
        }

        this.scheduleDraftSave();

        const needsRender =
          field === 'enabled' ||
          field === 'title' ||
          field === 'layoutType' ||
          field === 'required' ||
          field === 'rowRules.repeatable';

        if (needsRender) {
          this.render();
        }

        return;
      }

      if (templateField) {
  const field =
    templateField.dataset
      .aaTemplateField;

  if (field === 'clientId') {
    await this.loadClientTemplate(
      templateField.value
    );

    return;
  }

  this.template[field] =
    templateField.value;

  this.scheduleDraftSave();
  this.render();
}
    });

    this.root.addEventListener('click', (event) => {
      const settingsSummary = event.target.closest('.aa-template-builder__section-options > details > summary');
      if (settingsSummary) {
        const selected = settingsSummary.parentElement;
        this.root.querySelectorAll('.aa-template-builder__section-options > details[open]').forEach((panel) => {
          if (panel !== selected) panel.open = false;
        });
      }
      const publishButton =
        event.target.closest(
          '[data-aa-template-publish]'
        );

      const enableSectionButton =
        event.target.closest(
          '[data-aa-template-enable-section]'
        );
      const sectionButton = event.target.closest('[data-aa-template-select-section]');
      const tabButton = event.target.closest('[data-aa-template-section-tab]');
      const addCustomButton = event.target.closest('[data-aa-template-add-custom-section]');
      const cancelCustomButton = event.target.closest('[data-aa-template-cancel-custom-section]');
      const editFieldButton = event.target.closest('[data-aa-template-edit-field]');
      const deleteFieldButton = event.target.closest('[data-aa-template-delete-field]');
      const moveFieldUpButton = event.target.closest('[data-aa-template-move-field-up]');
      const moveFieldDownButton = event.target.closest('[data-aa-template-move-field-down]');

      const viewModeButton =
        event.target.closest(
          '[data-aa-template-view-mode]'
        );

      const previewDeviceButton =
        event.target.closest(
          '[data-aa-template-preview-device]'
        );

      const widthButton =
        event.target.closest(
          '[data-aa-template-field-width]'
        );

      if (viewModeButton) {
        this.viewMode =
          viewModeButton.dataset
            .aaTemplateViewMode === 'preview'
            ? 'preview'
            : 'edit';

        this.render();
        return;
      }

      if (previewDeviceButton) {
        this.previewDevice =
          previewDeviceButton.dataset
            .aaTemplatePreviewDevice === 'mobile'
            ? 'mobile'
            : 'desktop';

        this.render();
        return;
      }

      if (widthButton) {
        const section =
          this.template.sections.find(
            (item) =>
              item.id ===
              widthButton.dataset
                .aaTemplateFieldSection
          );

        const field =
          section?.fields.find(
            (item) =>
              item.id ===
              widthButton.dataset
                .aaTemplateFieldId
          );

        if (field) {
          field.width =
            widthButton.dataset
              .aaTemplateFieldWidth ||
            '50%';

          this.scheduleDraftSave();
          this.render();
        }

        return;
      }

      if (publishButton) {
        this.publishTemplateChanges();
        return;
      }

      if (enableSectionButton) {
        const section =
          this.template.sections.find(
            (item) =>
              item.id ===
              enableSectionButton
                .dataset
                .aaTemplateEnableSection
          );

        if (section) {
          section.enabled = true;
          this.template.selectedSectionId =
            section.id;

          this.scheduleDraftSave();
          this.render();
        }

        return;
      }

      if (sectionButton) {
        this.template.selectedSectionId = sectionButton.dataset.aaTemplateSelectSection;
        this.render();
        return;
      }

      if (tabButton) {
        const section = this.template.sections.find((item) => item.id === tabButton.dataset.aaTemplateSectionTab);
        if (section) section.activeTab = tabButton.dataset.aaTemplateTab;
        this.render();
        return;
      }

      if (addCustomButton) {
        this.isAddingCustomSection = true;
        this.render();

        const input = this.root.querySelector(
          '[data-aa-template-custom-section-name]'
        );

        input?.focus();
        return;
      }

      if (cancelCustomButton) {
        this.isAddingCustomSection = false;
        this.render();
        return;
      }

      if (editFieldButton) {
        const fieldId = editFieldButton.dataset.aaTemplateEditField;

        this.expandedFieldId =
          this.expandedFieldId === fieldId
            ? ''
            : fieldId;

        this.render();
        return;
      }

      if (deleteFieldButton) {
        this.deleteField(
          deleteFieldButton.dataset.aaTemplateFieldSection,
          deleteFieldButton.dataset.aaTemplateDeleteField
        );

        if (
          this.expandedFieldId ===
          deleteFieldButton.dataset.aaTemplateDeleteField
        ) {
          this.expandedFieldId = '';
        }

        return;
      }

      if (moveFieldUpButton) {
        this.moveField(moveFieldUpButton.dataset.aaTemplateFieldSection, moveFieldUpButton.dataset.aaTemplateMoveFieldUp, -1);
        return;
      }

      if (moveFieldDownButton) {
        this.moveField(moveFieldDownButton.dataset.aaTemplateFieldSection, moveFieldDownButton.dataset.aaTemplateMoveFieldDown, 1);
      }
    });
  }

  getCanvasDropPosition(
    canvas,
    event,
    span = 1
  ) {
    const rect =
      canvas.getBoundingClientRect();

    const styles =
      window.getComputedStyle(
        canvas
      );

    const paddingLeft =
      parseFloat(
        styles.paddingLeft
      ) || 0;

    const paddingRight =
      parseFloat(
        styles.paddingRight
      ) || 0;

    const paddingTop =
      parseFloat(
        styles.paddingTop
      ) || 0;

    const columnGap =
      parseFloat(
        styles.columnGap
      ) || 0;

    const rowGap =
      parseFloat(
        styles.rowGap
      ) || 0;

    const rowHeight =
      parseFloat(
        styles.gridAutoRows
      ) || 112;

    const device =
      canvas.dataset
        .aaTemplateLayoutDevice ||
      this.previewDevice;

    const columns =
      this.getLayoutColumns(
        device
      );

    const innerWidth =
      Math.max(
        1,
        rect.width -
          paddingLeft -
          paddingRight -
          columnGap *
            (columns - 1)
      );

    const columnWidth =
      innerWidth / columns;

    const relativeX =
      Math.max(
        0,
        event.clientX -
          rect.left -
          paddingLeft
      );

    const relativeY =
      Math.max(
        0,
        event.clientY -
          rect.top -
          paddingTop
      );

    let col =
      Math.floor(
        relativeX /
          (columnWidth + columnGap)
      ) + 1;

    const safeSpan =
      Math.max(
        1,
        Math.min(
          Number(span) || 1,
          columns
        )
      );

    col =
      Math.max(
        1,
        Math.min(
          col,
          columns - safeSpan + 1
        )
      );

    const row =
      Math.max(
        1,
        Math.floor(
          relativeY /
            (rowHeight + rowGap)
        ) + 1
      );

    return {
      row,
      col,
      span: safeSpan
    };
  }

  bindLayoutInteractions() {
    this.root.addEventListener(
      'dragstart',
      (event) => {
        const handle =
          event.target.closest(
            '[data-aa-template-drag-field]'
          );

        if (!handle) {
          return;
        }

        const fieldId =
          handle.dataset
            .aaTemplateDragField;

        const sectionId =
          handle.dataset
            .aaTemplateFieldSection;

        this.draggedLayoutField = {
          fieldId,
          sectionId,
          device:
            this.previewDevice
        };

        const card =
          handle.closest(
            '[data-aa-template-layout-field]'
          );

        card?.classList.add(
          'is-dragging'
        );

        if (event.dataTransfer) {
          event.dataTransfer.effectAllowed =
            'move';

          event.dataTransfer.setData(
            'text/plain',
            fieldId || ''
          );
        }
      }
    );

    this.root.addEventListener(
      'dragover',
      (event) => {
        const canvas =
          event.target.closest(
            '[data-aa-template-layout-canvas]'
          );

        if (
          !canvas ||
          !this.draggedLayoutField
        ) {
          return;
        }

        event.preventDefault();

        if (event.dataTransfer) {
          event.dataTransfer.dropEffect =
            'move';
        }

        canvas.classList.add(
          'is-drag-target'
        );
      }
    );

    this.root.addEventListener(
      'drop',
      (event) => {
        const canvas =
          event.target.closest(
            '[data-aa-template-layout-canvas]'
          );

        if (
          !canvas ||
          !this.draggedLayoutField
        ) {
          return;
        }

        event.preventDefault();

        const {
          sectionId,
          fieldId
        } =
          this.draggedLayoutField;

        const section =
          this.template.sections.find(
            (item) =>
              String(item.id) ===
              String(sectionId)
          );

        const field =
          section?.fields?.find(
            (item) =>
              String(item.id) ===
              String(fieldId)
          );

        if (!section || !field) {
          return;
        }

        const device =
          canvas.dataset
            .aaTemplateLayoutDevice ||
          this.previewDevice;

        const layout =
          this.getFieldLayout(
            section,
            field,
            device
          );

        const position =
          this.getCanvasDropPosition(
            canvas,
            event,
            layout.span
          );

        this.draggedLayoutField =
          null;

        this.placeLayoutField(
          sectionId,
          fieldId,
          device,
          position.row,
          position.col
        );
      }
    );

    this.root.addEventListener(
      'dragend',
      () => {
        this.draggedLayoutField =
          null;

        this.root
          .querySelectorAll(
            '.is-dragging, .is-drag-target'
          )
          .forEach((element) => {
            element.classList.remove(
              'is-dragging',
              'is-drag-target'
            );
          });
      }
    );

    this.root.addEventListener(
      'pointerdown',
      (event) => {
        const handle =
          event.target.closest(
            '[data-aa-template-resize-field]'
          );

        if (!handle) {
          return;
        }

        event.preventDefault();

        const card =
          handle.closest(
            '[data-aa-template-layout-field]'
          );

        const canvas =
          handle.closest(
            '[data-aa-template-layout-canvas]'
          );

        if (!card || !canvas) {
          return;
        }

        const sectionId =
          handle.dataset
            .aaTemplateFieldSection;

        const fieldId =
          handle.dataset
            .aaTemplateResizeField;

        const device =
          canvas.dataset
            .aaTemplateLayoutDevice ||
          this.previewDevice;

        const section =
          this.template.sections.find(
            (item) =>
              String(item.id) ===
              String(sectionId)
          );

        const field =
          section?.fields?.find(
            (item) =>
              String(item.id) ===
              String(fieldId)
          );

        if (!section || !field) {
          return;
        }

        const layout =
          this.getFieldLayout(
            section,
            field,
            device
          );

        const resizeState = {
          sectionId,
          fieldId,
          device,
          card,
          canvas,
          col:
            Number(layout.col) || 1,
          span:
            Number(layout.span) || 1
        };

        this.resizingLayoutField =
          resizeState;

        card.classList.add(
          'is-resizing'
        );

        const onMove = (moveEvent) => {
          if (
            this.resizingLayoutField !==
            resizeState
          ) {
            return;
          }

          const position =
            this.getCanvasDropPosition(
              canvas,
              moveEvent,
              1
            );

          const columns =
            this.getLayoutColumns(
              device
            );

          const endColumn =
            Math.max(
              resizeState.col,
              Math.min(
                position.col,
                columns
              )
            );

          const span =
            Math.max(
              1,
              endColumn -
                resizeState.col +
                1
            );

          resizeState.span =
            Math.min(
              span,
              columns -
                resizeState.col +
                1
            );

          card.style.gridColumn =
            `${resizeState.col} / span ${resizeState.span}`;
        };

        const onUp = () => {
          document.removeEventListener(
            'pointermove',
            onMove
          );

          document.removeEventListener(
            'pointerup',
            onUp
          );

          this.resizingLayoutField =
            null;

          card.classList.remove(
            'is-resizing'
          );

          this.resizeLayoutField(
            sectionId,
            fieldId,
            device,
            resizeState.span
          );
        };

        document.addEventListener(
          'pointermove',
          onMove
        );

        document.addEventListener(
          'pointerup',
          onUp,
          { once: true }
        );
      }
    );
  }

  deleteField(sectionId, fieldId) {
    const section = this.template.sections.find((item) => item.id === sectionId);
    if (!section) return;

    section.fields = section.fields.filter((field) => field.id !== fieldId);
    this.scheduleDraftSave();
    this.render();
  }

  moveField(sectionId, fieldId, direction) {
    const section = this.template.sections.find((item) => item.id === sectionId);
    if (!section) return;

    const currentIndex = section.fields.findIndex((field) => field.id === fieldId);
    const nextIndex = currentIndex + direction;

    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= section.fields.length) return;

    const [field] = section.fields.splice(currentIndex, 1);
    section.fields.splice(nextIndex, 0, field);

    this.scheduleDraftSave();
    this.render();
  }

  render() {
    const container =
      this.root.querySelector(
        '[data-aa-template-builder], [data-aa-playbook-builder]'
      );

    if (!container) return;

    const selectedSection =
      this.getSelectedSection();

    if (selectedSection) {
      this.ensureSectionLayouts(
        selectedSection
      );
    }

    const summary =
      this.getSummary();

    const client =
      this.clients.find(
        (item) =>
          String(item.id) ===
          String(this.template.clientId)
      ) || null;

    const disabledSections =
      [...this.template.sections]
        .filter(
          (section) =>
            !section.enabled
        )
        .sort(
          (a, b) =>
            Number(a.displayOrder) -
            Number(b.displayOrder)
        );

    container.innerHTML = `
      <div class="aa-schema-builder aa-schema-builder--premium">

        <section class="aa-template-builder__commandbar">
          <div class="aa-template-builder__identity">
            <p class="aa-workspace__eyebrow">
              Client Template
            </p>

            <div class="aa-template-builder__identity-row">
              ${client?.client_code ? `
                <span class="aa-client-code-pill aa-client-code-pill--large">
                  ${this.escapeHtml(client.client_code)}
                </span>
              ` : ''}

              <h2>
                ${this.escapeHtml(
                  client?.business_name ||
                  'Select a client'
                )}
              </h2>
            </div>

            <p>
              ${client
                ? `Monthly Marketing Playbook Template · ${this.baseRevision ? `Published v${this.baseRevision}` : 'Not published'}`
                : 'Choose a client to open their template workspace.'
              }
            </p>
          </div>

          <div class="aa-template-builder__selectors aa-template-builder__selectors--simple">
            <label>
              <span>Client</span>

              <select data-aa-template-field="clientId">
                <option value="">
                  Select Client
                </option>

                ${this.clients.map((item) => `
                  <option
                    value="${item.id}"
                    ${String(this.template.clientId) === String(item.id) ? 'selected' : ''}
                  >
                    ${this.escapeHtml(
                      item.client_code
                        ? `${item.client_code} · ${item.business_name}`
                        : item.business_name ||
                          'Unnamed Client'
                    )}
                  </option>
                `).join('')}
              </select>
            </label>
          </div>

          ${client ? `
            <div class="aa-template-builder__publish">
              <div class="aa-template-builder__draft-status">
                <span class="aa-template-builder__status-dot"></span>

                <div>
                  <strong>
                    ${this.escapeHtml(
                      this.getDraftStatusLabel()
                    )}
                  </strong>

                  <small>
                    ${summary.sections} enabled ·
                    ${summary.fields} fields
                  </small>
                </div>
              </div>

              <label class="aa-template-builder__effective">
                <span>Publish starting</span>

                <input
                  type="month"
                  value="${this.escapeHtml(
                    this.publishMonth
                  )}"
                  data-aa-template-publish-month
                >
              </label>

              <div class="aa-template-builder__publish-actions">
                <button
                  class="aa-button aa-button--primary"
                  type="button"
                  data-aa-template-publish
                  ${this.isPublishing ? 'disabled' : ''}
                >
                  ${this.isPublishing
                    ? 'Publishing...'
                    : 'Publish Changes'
                  }
                </button>

                <a
                  class="aa-button aa-button--secondary"
                  href="/pages/workspace-current-playbook?aa_admin_client=${encodeURIComponent(this.template.clientId)}"
                >
                  View Playbook
                </a>
              </div>
            </div>
          ` : ''}
        </section>

        ${this.builderMessage ? `
          <div class="aa-template-builder__message">
            ${this.escapeHtml(
              this.builderMessage
            )}
          </div>
        ` : ''}

        ${!client ? `
          <section class="aa-schema-builder__client-empty">
            <div>
              <p class="aa-card__label">
                Template Workspace
              </p>

              <h2>
                Select a client to begin
              </h2>

              <p>
                Open a client template to configure sections,
                fields, and the Playbook layout.
              </p>
            </div>
          </section>
        ` : `
          <div class="aa-schema-builder__workspace aa-schema-builder__workspace--live-builder">

            <aside class="aa-schema-builder__section-sidebar">
              <div class="aa-schema-builder__section-sidebar-header">
                <div>
                  <p class="aa-card__label">
                    Sections
                  </p>

                  <small>
                    ${summary.sections} enabled
                  </small>
                </div>

                ${!this.isAddingCustomSection ? `
                  <button
                    class="aa-button aa-button--small"
                    type="button"
                    data-aa-template-add-custom-section
                  >
                    + Custom
                  </button>
                ` : ''}
              </div>

              ${this.isAddingCustomSection ? `
                <form
                  class="aa-schema-builder__custom-section-form"
                  data-aa-template-custom-section-form
                >
                  <label>
                    <span>Section Name</span>

                    <input
                      type="text"
                      data-aa-template-custom-section-name
                      placeholder="Section name"
                      autocomplete="off"
                    >
                  </label>

                  <div class="aa-template-builder__inline-actions">
                    <button
                      class="aa-button aa-button--primary aa-button--small"
                      type="submit"
                    >
                      Add
                    </button>

                    <button
                      class="aa-button aa-button--small"
                      type="button"
                      data-aa-template-cancel-custom-section
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ` : ''}

              <div class="aa-schema-builder__section-list">
                ${this.getEnabledSections()
                  .map(
                    (section) =>
                      this.renderSectionListItem(section)
                  )
                  .join('')
                }
              </div>

              ${disabledSections.length ? `
                <details class="aa-template-builder__available">
                  <summary>
                    <span>
                      Add Sections
                    </span>

                    <small>
                      ${disabledSections.length}
                    </small>
                  </summary>

                  <div class="aa-template-builder__available-list">
                    ${disabledSections.map(
                      (section) =>
                        this.renderAvailableSectionItem(
                          section
                        )
                    ).join('')}
                  </div>
                </details>
              ` : ''}
            </aside>

            <main class="aa-schema-builder__editor aa-template-builder__live-editor">
              ${selectedSection
                ? this.renderSectionEditor(
                    selectedSection
                  )
                : '<p>Select a section.</p>'
              }
            </main>

          </div>
        `}

      </div>
    `;
  }

  renderAvailableSectionItem(section) {
    return `
      <button
        type="button"
        class="aa-template-builder__available-item"
        data-aa-template-enable-section="${this.escapeHtml(section.id)}"
      >
        <span>
          <strong>
            ${this.escapeHtml(section.title)}
          </strong>

          <small>
            ${(section.fields || []).length}
            fields
          </small>
        </span>

        <span aria-hidden="true">
          +
        </span>
      </button>
    `;
  }

  renderSectionListItem(section) {
    const isSelected = section.id === this.template.selectedSectionId;

    return `
      <button
        class="aa-schema-builder__section-item ${isSelected ? 'is-selected' : ''} ${section.enabled ? 'is-enabled' : ''}"
        type="button"
        data-aa-template-select-section="${section.id}"
      >
        <span>
          <strong>${this.escapeHtml(section.title)}</strong>
          <small>
  ${this.escapeHtml(section.layoutType || 'Marketing Cards')}
  · ${section.enabled ? 'Enabled' : 'Disabled'}
  · ${(section.fields || []).length} fields
</small>
        </span>
        <span class="aa-schema-builder__section-dot"></span>
      </button>
    `;
  }

  renderSectionEditor(section) {
    this.ensureSectionLayouts(section);

    return `
      <div class="aa-template-builder__live-header">
        <div>
          <p class="aa-card__label">
            Section Layout
          </p>

          <h2>
            ${this.escapeHtml(section.title)}
          </h2>

          <p>
            Drag fields where you want them.
            Drag the right edge to resize.
          </p>
        </div>

        <div class="aa-template-builder__live-header-actions">
          <div
            class="aa-template-builder__device-toggle"
            aria-label="Layout device"
          >
            <button
              type="button"
              class="${this.previewDevice === 'desktop' ? 'is-active' : ''}"
              data-aa-template-preview-device="desktop"
            >
              Desktop
            </button>

            <button
              type="button"
              class="${this.previewDevice === 'mobile' ? 'is-active' : ''}"
              data-aa-template-preview-device="mobile"
            >
              Mobile
            </button>
          </div>

          ${this.renderSwitch(
            section.id,
            'enabled',
            section.enabled,
            'Enabled'
          )}
        </div>
      </div>

      <form
        class="aa-template-builder__quick-add"
        data-aa-template-add-field-form="${section.id}"
      >
        <input
          type="text"
          data-aa-template-new-field-label
          placeholder="Add a field..."
        >

        <select data-aa-template-new-field-type>
          ${this.getFieldTypes().map(
            (type) => `
              <option value="${type}">
                ${type}
              </option>
            `
          ).join('')}
        </select>

        <button
          class="aa-button aa-button--primary"
          type="submit"
        >
          Add Field
        </button>
      </form>

      ${this.renderLayoutCanvas(section)}

      ${this.expandedFieldId
        ? this.renderFieldDetailsPanel(section)
        : ''
      }

      <div class="aa-template-builder__section-options">
        <details class="aa-schema-builder__group">
          <summary>
            <span>
              <strong>Section Settings</strong>
              <small>Name and help text</small>
            </span>
          </summary>

          <div class="aa-schema-builder__group-body">
            ${this.renderBasicsGroup(section)}
          </div>
        </details>

        <details class="aa-schema-builder__group">
          <summary>
            <span>
              <strong>Entry Settings</strong>
              <small>
                ${section.rowRules?.repeatable
                  ? `Up to ${section.rowRules.maximumRows || 1} entries`
                  : 'Single entry'
                }
              </small>
            </span>
          </summary>

          <div class="aa-schema-builder__group-body">
            ${this.renderEntryRulesGroup(section)}
          </div>
        </details>
      </div>
    `;
  }

  renderLayoutCanvas(section) {
    this.ensureSectionLayouts(section);

    const device =
      this.previewDevice;

    const columns =
      this.getLayoutColumns(device);

    const maxRow =
      this.getLayoutMaxRow(
        section,
        device
      );

    return `
      <section class="aa-template-layout-shell">
        <div class="aa-template-layout-shell__meta">
          <span>
            ${device === 'desktop'
              ? 'Desktop layout'
              : 'Mobile layout'
            }
          </span>

          <small>
            ${columns}-column snap grid
          </small>
        </div>

        <div
          class="aa-template-layout-canvas aa-template-layout-canvas--${this.escapeHtml(device)}"
          data-aa-template-layout-canvas
          data-aa-template-layout-section="${this.escapeHtml(section.id)}"
          data-aa-template-layout-device="${this.escapeHtml(device)}"
          style="--aa-template-columns:${columns};--aa-template-rows:${Math.max(maxRow + 1, 3)};"
        >
          ${(section.fields || [])
            .map(
              (field) =>
                this.renderLayoutField(
                  section,
                  field,
                  device
                )
            )
            .join('')
          }
        </div>
      </section>
    `;
  }

  renderLayoutField(
    section,
    field,
    device
  ) {
    const layout =
      this.getFieldLayout(
        section,
        field,
        device
      );

    return `
      <article
        class="aa-template-layout-field ${this.expandedFieldId === field.id ? 'is-selected' : ''}"
        data-aa-template-layout-field="${this.escapeHtml(field.id)}"
        data-aa-template-layout-section="${this.escapeHtml(section.id)}"
        style="
          grid-column:${layout.col} / span ${layout.span};
          grid-row:${layout.row};
        "
      >
        <div class="aa-template-layout-field__top">
          <button
            type="button"
            class="aa-template-layout-field__drag"
            draggable="true"
            data-aa-template-drag-field="${this.escapeHtml(field.id)}"
            data-aa-template-field-section="${this.escapeHtml(section.id)}"
            aria-label="Drag ${this.escapeHtml(field.label)}"
            title="Drag to move"
          >
            ⋮⋮
          </button>

          <div class="aa-template-layout-field__identity">
            <strong>
              ${this.escapeHtml(field.label)}
            </strong>

            <small>
              ${this.escapeHtml(field.type)}
            </small>
          </div>

          <button
            type="button"
            class="aa-template-layout-field__details"
            data-aa-template-edit-field="${this.escapeHtml(field.id)}"
            aria-label="Edit ${this.escapeHtml(field.label)}"
          >
            Edit
          </button>
        </div>

        <div class="aa-template-layout-field__control">
          ${this.renderBuilderFieldControl(field)}
        </div>

        <button
          type="button"
          class="aa-template-layout-field__resize"
          data-aa-template-resize-field="${this.escapeHtml(field.id)}"
          data-aa-template-field-section="${this.escapeHtml(section.id)}"
          aria-label="Resize ${this.escapeHtml(field.label)}"
          title="Drag to resize"
        ></button>
      </article>
    `;
  }

  renderBuilderFieldControl(field) {
    const label =
      this.escapeHtml(field.label);

    if (
      field.type === 'Paragraph' ||
      field.type === 'Rich Text'
    ) {
      return `
        <div class="aa-template-layout-control aa-template-layout-control--textarea">
          ${label}
        </div>
      `;
    }

    if (
      field.type === 'Image Upload' ||
      field.type === 'File Upload'
    ) {
      return `
        <div class="aa-template-layout-control aa-template-layout-control--upload">
          ${field.type === 'Image Upload'
            ? 'Image'
            : 'File'
          }
        </div>
      `;
    }

    if (
      field.type === 'Checkbox' ||
      field.type === 'Toggle'
    ) {
      return `
        <div class="aa-template-layout-control aa-template-layout-control--toggle">
          <span></span>
          Off
        </div>
      `;
    }

    if (
      field.type === 'Dropdown' ||
      field.type === 'Multi Select'
    ) {
      return `
        <div class="aa-template-layout-control aa-template-layout-control--select">
          Select
          <span>⌄</span>
        </div>
      `;
    }

    return `
      <div class="aa-template-layout-control">
        ${field.placeholder
          ? this.escapeHtml(field.placeholder)
          : label
        }
      </div>
    `;
  }

  renderFieldDetailsPanel(section) {
    const field =
      section.fields.find(
        (item) =>
          String(item.id) ===
          String(this.expandedFieldId)
      );

    if (!field) {
      return '';
    }

    return `
      <section class="aa-template-field-details">
        <div class="aa-template-field-details__header">
          <div>
            <p class="aa-card__label">
              Field Details
            </p>

            <h3>
              ${this.escapeHtml(field.label)}
            </h3>
          </div>

          <button
            type="button"
            class="aa-button aa-button--small"
            data-aa-template-edit-field="${this.escapeHtml(field.id)}"
          >
            Close
          </button>
        </div>

        <div class="aa-schema-builder__grid aa-schema-builder__grid--field">
          <label class="aa-playbook-builder__field">
            <span>Label</span>

            <input
              type="text"
              data-aa-template-field="${this.escapeHtml(field.id)}"
              data-aa-template-field-section="${this.escapeHtml(section.id)}"
              data-aa-template-field-property="label"
              value="${this.escapeHtml(field.label)}"
            >
          </label>

          <label class="aa-playbook-builder__field">
            <span>Type</span>

            <select
              data-aa-template-field="${this.escapeHtml(field.id)}"
              data-aa-template-field-section="${this.escapeHtml(section.id)}"
              data-aa-template-field-property="type"
            >
              ${this.getFieldTypes().map(
                (type) => `
                  <option
                    value="${type}"
                    ${field.type === type ? 'selected' : ''}
                  >
                    ${type}
                  </option>
                `
              ).join('')}
            </select>
          </label>

          ${this.renderFieldSwitch(
            section.id,
            field.id,
            'required',
            field.required,
            'Required'
          )}

          <label class="aa-playbook-builder__field">
            <span>Placeholder</span>

            <input
              type="text"
              data-aa-template-field="${this.escapeHtml(field.id)}"
              data-aa-template-field-section="${this.escapeHtml(section.id)}"
              data-aa-template-field-property="placeholder"
              value="${this.escapeHtml(field.placeholder || '')}"
            >
          </label>

          <label class="aa-playbook-builder__field">
            <span>Help Text</span>

            <input
              type="text"
              data-aa-template-field="${this.escapeHtml(field.id)}"
              data-aa-template-field-section="${this.escapeHtml(section.id)}"
              data-aa-template-field-property="helpText"
              value="${this.escapeHtml(field.helpText || '')}"
            >
          </label>
        </div>

        <div class="aa-template-field-details__footer">
          <button
            class="aa-button aa-button--small aa-button--ghost"
            type="button"
            data-aa-template-delete-field="${this.escapeHtml(field.id)}"
            data-aa-template-field-section="${this.escapeHtml(section.id)}"
          >
            Delete Field
          </button>
        </div>
      </section>
    `;
  }

renderBasicsGroup(section) {
  return `
    <div class="aa-schema-builder__grid">
      ${this.renderSectionField(section, 'title', 'Section Name')}

      <label class="aa-playbook-builder__field">
        <span>Section Type</span>
        <select
          data-aa-template-section="${section.id}"
          data-aa-template-section-field="layoutType"
        >
          ${this.getLayoutOptions().map((option) => `
            <option
              value="${option}"
              ${section.layoutType === option ? 'selected' : ''}
            >
              ${option}
            </option>
          `).join('')}
        </select>
      </label>

      ${this.renderSectionField(section, 'helpText', 'Description / Help Text')}

      ${this.renderSwitch(
        section.id,
        'required',
        section.required,
        'Required'
      )}
    </div>
  `;
}

renderEntryRulesGroup(section) {
  if (!section.rowRules.repeatable) {
    return `
      <div class="aa-schema-builder__entry-simple">
        <p>This section appears once.</p>

        ${this.renderSwitch(
          section.id,
          'rowRules.repeatable',
          section.rowRules.repeatable,
          'Allow Multiple Entries'
        )}
      </div>
    `;
  }

  const entryLabel =
    section.rowRules.rowLabelText ||
    section.title.replace(/s$/, '') ||
    'Entry';

  return `
    <div class="aa-schema-builder__entry-simple">
      <label class="aa-playbook-builder__field">
        <span>How many?</span>

        <div class="aa-schema-builder__entry-limit">
          <span>Allow up to</span>

          <input
            type="number"
            min="1"
            data-aa-template-section="${section.id}"
            data-aa-template-section-field="rowRules.maximumRows"
            value="${this.escapeHtml(section.rowRules.maximumRows || 1)}"
          >

          <span>${this.escapeHtml(entryLabel)} entries</span>
        </div>
      </label>

      <label class="aa-playbook-builder__field">
        <span>Add Button Label</span>
        <input
          type="text"
          data-aa-template-section="${section.id}"
          data-aa-template-section-field="rowRules.addButtonText"
          value="${this.escapeHtml(section.rowRules.addButtonText || `Add ${entryLabel}`)}"
        >
      </label>

      <details class="aa-schema-builder__entry-advanced">
        <summary>Advanced Entry Settings</summary>

        <div class="aa-schema-builder__grid">
          ${this.renderSwitch(
            section.id,
            'rowRules.repeatable',
            section.rowRules.repeatable,
            'Allow Multiple Entries'
          )}

          ${this.renderRowRuleField(
            section,
            'minimumRows',
            'Minimum Rows'
          )}

          ${this.renderRowRuleField(
            section,
            'defaultRows',
            'Starting Rows'
          )}

          ${this.renderRowRuleField(
            section,
            'rowLabelText',
            'Entry Heading',
            'text'
          )}

          ${this.renderSwitch(
            section.id,
            'rowRules.allowDuplicateRows',
            section.rowRules.allowDuplicateRows,
            'Allow Duplicate'
          )}

          ${this.renderSwitch(
            section.id,
            'rowRules.allowDeleteRows',
            section.rowRules.allowDeleteRows,
            'Allow Delete'
          )}

          ${this.renderSwitch(
            section.id,
            'rowRules.allowReorderRows',
            section.rowRules.allowReorderRows,
            'Allow Reorder'
          )}
        </div>
      </details>
    </div>
  `;
}

renderAdvancedGroup(section) {
  return `
    <div class="aa-schema-builder__grid">
      ${this.renderSectionField(
        section,
        'displayOrder',
        'Display Order',
        'number'
      )}
    </div>
  `;
}

  renderSettingsTab(section) {
    return `
      <div class="aa-schema-builder__panel">
        <div class="aa-schema-builder__grid">
          ${this.renderSectionField(section, 'title', 'Section Title')}
          <label class="aa-playbook-builder__field">
            <span>Layout Type</span>
            <select data-aa-template-section="${section.id}" data-aa-template-section-field="layoutType">
              ${this.getLayoutOptions().map((option) => `
                <option value="${option}" ${section.layoutType === option ? 'selected' : ''}>${option}</option>
              `).join('')}
            </select>
          </label>
          ${this.renderSectionField(section, 'displayOrder', 'Display Order', 'number')}
          ${this.renderSectionField(section, 'helpText', 'Help Text')}
        </div>

        <h3>Row Rules</h3>
        <div class="aa-schema-builder__grid">
          ${this.renderSwitch(section.id, 'rowRules.repeatable', section.rowRules.repeatable, 'Repeatable Rows')}
          ${this.renderRowRuleField(section, 'minimumRows', 'Minimum Rows')}
          ${this.renderRowRuleField(section, 'maximumRows', 'Maximum Rows')}
          ${this.renderRowRuleField(section, 'defaultRows', 'Default Rows')}
          ${this.renderSwitch(section.id, 'rowRules.allowDuplicateRows', section.rowRules.allowDuplicateRows, 'Allow Duplicate Rows')}
          ${this.renderSwitch(section.id, 'rowRules.allowDeleteRows', section.rowRules.allowDeleteRows, 'Allow Delete Rows')}
          ${this.renderSwitch(section.id, 'rowRules.allowReorderRows', section.rowRules.allowReorderRows, 'Allow Reorder Rows')}
          ${this.renderRowRuleField(section, 'rowLabelText', 'Row Heading', 'text')}
          ${this.renderRowRuleField(section, 'addButtonText', 'Add Button Text', 'text')}
        </div>
      </div>
    `;
  }

  renderFieldsTab(section) {
    return `
      <div class="aa-schema-builder__panel">
        <form class="aa-schema-builder__add-field" data-aa-template-add-field-form="${section.id}">
          <input type="text" data-aa-template-new-field-label placeholder="Field label">
          <select data-aa-template-new-field-type>
            ${this.getFieldTypes().map((type) => `<option value="${type}">${type}</option>`).join('')}
          </select>
          <button class="aa-button aa-button--primary" type="submit">Add Field</button>
        </form>

        <div class="aa-schema-builder__field-list">
          ${(section.fields || []).map((field, index) => this.renderFieldCard(section, field, index)).join('')}
        </div>
      </div>
    `;
  }

    renderFieldCard(section, field, index) {
    const isExpanded =
      this.expandedFieldId === field.id;

    const width =
      ['25%', '50%', '75%', '100%']
        .includes(field.width)
        ? field.width
        : '50%';

    return `
      <article class="aa-schema-builder__field-card aa-schema-builder__field-card--simple ${isExpanded ? 'is-expanded' : ''}">
        <div class="aa-schema-builder__field-row">
          <div class="aa-schema-builder__field-order">
            <span>${index + 1}</span>

            <div>
              <button
                type="button"
                data-aa-template-move-field-up="${field.id}"
                data-aa-template-field-section="${section.id}"
                aria-label="Move ${this.escapeHtml(field.label)} up"
                ${index === 0 ? 'disabled' : ''}
              >
                ↑
              </button>

              <button
                type="button"
                data-aa-template-move-field-down="${field.id}"
                data-aa-template-field-section="${section.id}"
                aria-label="Move ${this.escapeHtml(field.label)} down"
                ${index === (section.fields || []).length - 1 ? 'disabled' : ''}
              >
                ↓
              </button>
            </div>
          </div>

          <div class="aa-schema-builder__field-identity">
            <strong>
              ${this.escapeHtml(field.label)}
            </strong>

            <small>
              ${this.escapeHtml(field.type)}
              ${field.required ? ' · Required' : ''}
            </small>
          </div>

          <div class="aa-template-builder__field-layout">
            <span>Width</span>

            <div class="aa-template-builder__width-buttons">
              ${['25%', '50%', '75%', '100%'].map(
                (option) => `
                  <button
                    type="button"
                    class="${width === option ? 'is-active' : ''}"
                    data-aa-template-field-width="${option}"
                    data-aa-template-field-id="${field.id}"
                    data-aa-template-field-section="${section.id}"
                    title="${this.escapeHtml(this.getFieldWidthLabel(option))}"
                  >
                    ${option.replace('%', '')}
                  </button>
                `
              ).join('')}
            </div>
          </div>

          <label class="aa-template-builder__row-break">
            <input
              type="checkbox"
              data-aa-template-field-new-row="${field.id}"
              data-aa-template-field-section="${section.id}"
              ${field.startNewRow ? 'checked' : ''}
            >

            <span>New row</span>
          </label>

          <button
            class="aa-button aa-button--small"
            type="button"
            data-aa-template-edit-field="${field.id}"
          >
            ${isExpanded ? 'Close' : 'Details'}
          </button>
        </div>

        ${isExpanded ? `
          <div class="aa-schema-builder__field-editor aa-schema-builder__field-editor--simple">
            <div class="aa-schema-builder__grid aa-schema-builder__grid--field">
              <label class="aa-playbook-builder__field">
                <span>Label</span>

                <input
                  type="text"
                  data-aa-template-field="${field.id}"
                  data-aa-template-field-section="${section.id}"
                  data-aa-template-field-property="label"
                  value="${this.escapeHtml(field.label)}"
                >
              </label>

              <label class="aa-playbook-builder__field">
                <span>Type</span>

                <select
                  data-aa-template-field="${field.id}"
                  data-aa-template-field-section="${section.id}"
                  data-aa-template-field-property="type"
                >
                  ${this.getFieldTypes().map(
                    (type) => `
                      <option
                        value="${type}"
                        ${field.type === type ? 'selected' : ''}
                      >
                        ${type}
                      </option>
                    `
                  ).join('')}
                </select>
              </label>

              ${this.renderFieldSwitch(
                section.id,
                field.id,
                'required',
                field.required,
                'Required'
              )}

              <label class="aa-playbook-builder__field">
                <span>Placeholder</span>

                <input
                  type="text"
                  data-aa-template-field="${field.id}"
                  data-aa-template-field-section="${section.id}"
                  data-aa-template-field-property="placeholder"
                  value="${this.escapeHtml(field.placeholder || '')}"
                >
              </label>

              <label class="aa-playbook-builder__field">
                <span>Help Text</span>

                <input
                  type="text"
                  data-aa-template-field="${field.id}"
                  data-aa-template-field-section="${section.id}"
                  data-aa-template-field-property="helpText"
                  value="${this.escapeHtml(field.helpText || '')}"
                >
              </label>
            </div>

            <div class="aa-schema-builder__field-danger">
              <button
                class="aa-button aa-button--small aa-button--ghost"
                type="button"
                data-aa-template-delete-field="${field.id}"
                data-aa-template-field-section="${section.id}"
              >
                Delete Field
              </button>
            </div>
          </div>
        ` : ''}
      </article>
    `;
  }

  renderPreviewTab(section) {
    const rows = Math.max(1, Number(section.rowRules?.defaultRows || 1));

    return `
      <div class="aa-schema-builder__preview">
        <div class="aa-schema-builder__preview-card">
          <div class="aa-playbook-form-section__header">
            <div class="aa-playbook-form-section__heading">
              <h3>${this.escapeHtml(section.title)}</h3>
              ${section.helpText ? `<p>${this.escapeHtml(section.helpText)}</p>` : ''}
            </div>
          </div>

          ${Array.from({ length: rows }).map((_, rowIndex) => `
            <div class="aa-schema-builder__preview-row">
              ${section.rowRules.repeatable ? `
  <p class="aa-card__label">
    ${this.escapeHtml(section.rowRules.rowLabelText || section.title || 'Item')} ${rowIndex + 1}
  </p>
` : ''}
              <div class="aa-schema-builder__preview-fields">
                ${(section.fields || []).map((field) => this.renderPreviewField(field)).join('')}
              </div>
            </div>
          `).join('')}

          ${section.rowRules.repeatable ? `
            <button class="aa-button aa-button--secondary" type="button">${this.escapeHtml(section.rowRules.addButtonText || 'Add Row')}</button>
          ` : ''}
        </div>
      </div>
    `;
  }

  renderPreviewField(field) {
    if (field.type === 'Heading') return `<h4 class="aa-schema-preview-heading">${this.escapeHtml(field.label)}</h4>`;
    if (field.type === 'Divider') return `<hr class="aa-schema-preview-divider">`;

    const span =
      this.getFieldSpan(field.width);

    const common = `
      <label
        class="aa-playbook-field"
        style="grid-column: ${field.startNewRow ? `1 / span ${span}` : `span ${span}`};"
      >
        <span class="aa-playbook-field__label">${this.escapeHtml(field.label)}${field.required ? ' *' : ''}</span>
    `;

    if (field.type === 'Paragraph' || field.type === 'Rich Text') {
      return `${common}<textarea class="aa-playbook-textarea" placeholder="${this.escapeHtml(field.placeholder)}"></textarea></label>`;
    }

    if (field.type === 'Dropdown' || field.type === 'Multi Select') {
      return `${common}<select class="aa-playbook-select"><option>${this.escapeHtml(field.placeholder || 'Select')}</option></select></label>`;
    }

    if (field.type === 'Image Upload' || field.type === 'File Upload') {
      return `${common}<div class="aa-playbook-placeholder">${this.escapeHtml(field.type)}</div></label>`;
    }

    if (field.type === 'Checkbox' || field.type === 'Toggle') {
      return `${common}<span class="aa-schema-switch-preview">Off</span></label>`;
    }

    const inputTypes = {
      Currency: 'number',
      Number: 'number',
      Date: 'date',
      Time: 'time',
      URL: 'url',
      Email: 'email',
      Phone: 'tel'
    };

    return `${common}<input class="aa-playbook-input" type="${inputTypes[field.type] || 'text'}" placeholder="${this.escapeHtml(field.placeholder)}"></label>`;
  }

  renderSectionField(section, field, label, type = 'text') {
    return `
      <label class="aa-playbook-builder__field">
        <span>${this.escapeHtml(label)}</span>
        <input type="${type}" data-aa-template-section="${section.id}" data-aa-template-section-field="${field}" value="${this.escapeHtml(section[field])}">
      </label>
    `;
  }

  renderRowRuleField(section, field, label, type = 'number') {
    return `
      <label class="aa-playbook-builder__field">
        <span>${this.escapeHtml(label)}</span>
        <input type="${type}" data-aa-template-section="${section.id}" data-aa-template-section-field="rowRules.${field}" value="${this.escapeHtml(section.rowRules[field])}">
      </label>
    `;
  }

  renderSwitch(sectionId, field, checked, label) {
    return `
      <label class="aa-schema-switch">
        <span>${this.escapeHtml(label)}</span>
        <input type="checkbox" data-aa-template-section="${sectionId}" data-aa-template-section-field="${field}" ${checked ? 'checked' : ''}>
        <i></i>
      </label>
    `;
  }

  renderFieldSwitch(sectionId, fieldId, property, checked, label) {
    return `
      <label class="aa-schema-switch">
        <span>${this.escapeHtml(label)}</span>
        <input type="checkbox" data-aa-template-field="${fieldId}" data-aa-template-field-section="${sectionId}" data-aa-template-field-property="${property}" ${checked ? 'checked' : ''}>
        <i></i>
      </label>
    `;
  }
}

window.AATemplateBuilder = AATemplateBuilder;